/**
 * Pure logic behind the Registry page's collapsed step rows + detail panel
 * (plans/2026-10-15-registry-page-ux.md): deep-link hash parsing, selection
 * navigation, one-line step summaries, the problems-only filter and the
 * top summary strip counts. No React, no DOM.
 */
import type { PublishStepRow, StepState } from "./publishSteps.ts";

// ── deep link: #run=<attemptKey>&step=<n> ─────────────────────────────────
export interface StepSelection {
  run: string | null;
  step: string | null;
}

export function parseStepHash(hash: string): StepSelection {
  const body = hash.replace(/^#/, "");
  const out: StepSelection = { run: null, step: null };
  if (!body) return out;
  for (const part of body.split("&")) {
    const i = part.indexOf("=");
    if (i < 0) continue;
    let key: string;
    let val: string;
    try {
      key = decodeURIComponent(part.slice(0, i));
      val = decodeURIComponent(part.slice(i + 1));
    } catch {
      continue;
    }
    if (key === "run" && val) out.run = val;
    if (key === "step" && /^\d+([a-z]|\.\d+)?$/.test(val)) out.step = val;
  }
  // A step without a run is meaningless; a run without a step is just a run.
  if (!out.run) out.step = null;
  return out;
}

export function formatStepHash(sel: StepSelection): string {
  if (!sel.run || !sel.step) return "";
  return `#run=${encodeURIComponent(sel.run)}&step=${encodeURIComponent(sel.step)}`;
}

// ── navigation ────────────────────────────────────────────────────────────
/** Every row in display order, sub-rows right after their parent. */
export function flattenRows(rows: readonly PublishStepRow[]): PublishStepRow[] {
  return rows.flatMap((r) => [r, ...flattenRows(r.subrows)]);
}

/** The step n moved by `delta` (clamped, no wrap); null when `current` is
 *  unknown or `order` is empty. */
export function neighborStep(
  order: readonly string[],
  current: string,
  delta: number,
): string | null {
  const i = order.indexOf(current);
  if (i < 0 || order.length === 0) return null;
  const j = Math.min(order.length - 1, Math.max(0, i + delta));
  return order[j] ?? null;
}

// ── problems filter ───────────────────────────────────────────────────────
export function isProblem(state: StepState): boolean {
  return state === "failed" || state === "degraded";
}

/** Rows to show: all, or only failed / degraded / running ones (a row stays
 *  when any of its sub-rows qualifies, and keeps all its sub-rows). */
export function filterRows(
  rows: readonly PublishStepRow[],
  problemsOnly: boolean,
): PublishStepRow[] {
  if (!problemsOnly) return [...rows];
  const keep = (r: PublishStepRow): boolean =>
    isProblem(r.state) || r.state === "running" || r.subrows.some(keep);
  return rows.filter(keep);
}

// ── summary strip ─────────────────────────────────────────────────────────
export type StateCounts = Record<StepState, number>;

export function countStates(rows: readonly PublishStepRow[]): StateCounts {
  const c: StateCounts = {
    pending: 0,
    running: 0,
    ok: 0,
    degraded: 0,
    failed: 0,
    skipped: 0,
  };
  for (const r of flattenRows(rows)) c[r.state]++;
  return c;
}

// ── one-line key fact ─────────────────────────────────────────────────────
function firstLine(s: string, max = 140): string {
  const line = (s.split("\n").find((l) => l.trim() !== "") ?? "").trim();
  return line.length > max ? `${line.slice(0, max - 1)}…` : line;
}

/** The one thing worth reading at a glance on a collapsed row. */
export function stepSummary(r: PublishStepRow): string {
  if (r.state === "running") {
    if (r.progress) return r.progress;
    return r.where;
  }
  if (r.reason && (r.state !== "ok" || r.off)) return firstLine(r.reason);
  if (r.state === "pending") return "waiting";
  const bad = r.views.find((v) => v.state !== "ok");
  if (bad) {
    return firstLine(
      `${bad.title}: ${bad.state === "partial" ? "partial" : "not measured"}${bad.reason ? ` - ${bad.reason}` : ""}`,
    );
  }
  const head = r.views.find((v) => v.headline);
  if (head?.headline) return firstLine(`${head.title}: ${head.headline}`);
  const d = r.details[0];
  if (d) return firstLine(`${d.label}: ${d.value}`);
  if (r.runnerBuilds > 0)
    return `${r.runnerBuilds} runner build${r.runnerBuilds === 1 ? "" : "s"} on ${r.where}`;
  return r.where;
}
